import React, { useState, useRef } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  Assignment as TaskIcon,
  ShoppingCart as ShopIcon,
  Palette as StyleIcon,
  AccountBalanceWallet as MoneyIcon,
  InstallMobile as PwaIcon,
  AddTask as CreateIcon,
  Storefront as ManageShopIcon,
  Notifications as NotifIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence, useMotionValue, useTransform } from 'framer-motion';
import { useThemeMode } from '../../contexts/ThemeContext';

const PINK = {
  main: '#e91e8c',
  light: '#f06292',
  dark: '#c2185b',
  hot: '#ff1493',
  bg: '#fce4ec',
  darkBg: '#2d1a24',
  gradient: 'linear-gradient(135deg, #e91e8c 0%, #f06292 50%, #ff1493 100%)',
  glow: '0 0 24px #e91e8c44, 0 8px 32px #e91e8c22',
};

const DEMO_CARDS = [
  { icon: null, title: 'Demo', text: 'Sinulle on luotu laitekohtainen demoversio, eivätkä muutokset vaikuta muihin. Demo nollaantuu 30 minuutin jälkeen.', isDemo: true },
  { icon: null, title: 'Testaa', text: 'Käytössäsi on (+1 pv) -nappi, jolla voit vaihtaa vuorokautta ja nollata päivän tehtävät.', isDemo: true },
  { icon: null, title: 'Aloita alusta', text: 'Käyttäjän PIN on 0000. Asetukset sivulla voit kirjautua ulos ja palata kirjautumiseen.', isDemo: true },
];

const CHILD_CARDS = [
  { icon: TaskIcon, title: 'Tehtävät', text: 'Tee päivittäisiä kotiaskareita ja kerrytä pisteitä sekä bonuskolikoita' },
  { icon: ShopIcon, title: 'Kauppa', text: 'Käytä kolikoita tavaroihin ja elämyksiin' },
  { icon: StyleIcon, title: 'Tyylittele hahmosi', text: 'Muokkaa profiilisi ulkoasua ja värimaailmaa' },
  { icon: MoneyIcon, title: 'Viikkoraha', text: 'Viikon pisteet muuttuvat kolikoiksi viikonloppuna' },
  { icon: PwaIcon, title: 'Asennettava sovellus', text: 'Asenna sovellus ja saa ilmoitukset uusista tehtävistä' },
];

const PARENT_CARDS = [
  { icon: CreateIcon, title: 'Kotiaskareiden luonti', text: 'Luo tehtäviä käyttäjille ja aseta pistemäärät sekä viikkotavoite' },
  { icon: ManageShopIcon, title: 'Kaupan hallinta', text: 'Muokkaa ja lisää tarjolla olevia palkintoja' },
  { icon: MoneyIcon, title: 'Viikkoraha', text: 'Jaa viikkoraha lauantaina tai anna sen jakautua automaattisesti sunnuntaina' },
  { icon: NotifIcon, title: 'Asennettava sovellus', text: 'Asenna sovellus ja saa ilmoituksen kun käyttäjä pyytää hyväksyntää' },
];

const STACK_CONFIG = [
  { scale: 1, y: 0, opacity: 1 },
  { scale: 0.94, y: 16, opacity: .9 },
  { scale: 0.88, y: 32, opacity: .8 },
];

const DISMISS_THRESHOLD = 120;

function TutorialCard({ card, index, isDark, onDismiss }) {
  const isTop = index === 0;
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-12, 12]);
  const exitDirectionRef = useRef({ x: 300, y: -100 });
  const config = STACK_CONFIG[index] || STACK_CONFIG[2];
  const hasDragged = useRef(false);
  const Icon = card.icon || null;

  const handleDragStart = () => { hasDragged.current = false; };

  const handleDrag = (_, info) => {
    if (Math.abs(info.offset.x) > 10 || Math.abs(info.offset.y) > 10) {
      hasDragged.current = true;
    }
  };

  const handleDragEnd = (_, info) => {
    const absX = Math.abs(info.offset.x);
    const absVelocityX = Math.abs(info.velocity.x);
    if (absX > DISMISS_THRESHOLD || absVelocityX > 500 || info.offset.y < -80) {
      exitDirectionRef.current = {
        x: info.offset.x > 0 ? 400 : -400,
        y: info.offset.y < -80 ? -200 : 0,
      };
      onDismiss();
    }
  };

  const handleTap = () => {
    if (!hasDragged.current) {
      exitDirectionRef.current = { x: 250, y: -80 };
      onDismiss();
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 60, scale: 0.8 }}
      animate={{
        opacity: config.opacity,
        y: config.y,
        scale: config.scale,
        transition: { type: 'spring', stiffness: 300, damping: 25, delay: index * 0.08 },
      }}
      exit={{
        x: exitDirectionRef.current.x,
        y: exitDirectionRef.current.y,
        opacity: 0,
        scale: 0.6,
        rotate: exitDirectionRef.current.x > 0 ? 15 : -15,
        transition: { duration: 0.35 },
      }}
      style={{
        position: 'absolute',
        top: 0, left: 0, right: 0,
        zIndex: 10 - index,
        x: isTop ? x : 0,
        rotate: isTop ? rotate : 0,
        touchAction: 'none',
      }}
      drag={isTop}
      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
      dragElastic={0.7}
      dragSnapToOrigin
      onDragStart={isTop ? handleDragStart : undefined}
      onDrag={isTop ? handleDrag : undefined}
      onDragEnd={isTop ? handleDragEnd : undefined}
      onTap={isTop ? handleTap : undefined}
      whileTap={isTop ? { scale: 0.97 } : undefined}
    >
      <Box sx={{
        bgcolor: card.isDemo ? '#0d0d1a' : (isDark ? 'background.default' : '#fff'),
        borderRadius: 4, p: 4,
        boxShadow: card.isDemo
          ? '0 8px 32px rgba(211,47,47,0.25), 0 0 0 1.5px #d32f2f'
          : (isDark
            ? `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${PINK.main}22`
            : `0 8px 32px ${PINK.main}12, 0 0 0 1px ${PINK.main}22`),
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        textAlign: 'center', minHeight: 280,
        justifyContent: 'center',
        cursor: isTop ? 'grab' : 'default',
        userSelect: 'none',
        border: card.isDemo ? '1.5px solid #d32f2f' : `1px solid ${isDark ? PINK.main + '22' : PINK.main + '15'}`,
      }}>
        {card.isDemo ? (
          <Typography variant="h2" sx={{ mb: 2 }}>🧪</Typography>
        ) : (
        <Box sx={{
          width: 72, height: 72, borderRadius: '16px',
          bgcolor: isDark ? `${PINK.main}15` : `${PINK.main}12`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
        }}>
          <Icon sx={{ fontSize: 36, color: isDark ? PINK.light : PINK.main }} />
        </Box>
        )}
        <Typography variant="caption" fontWeight={900} sx={{ color: '#d32f2f', letterSpacing: 1.5, display: card.isDemo ? 'block' : 'none', mb: 0.5 }}>
          #DEMO
        </Typography>
        <Typography variant="h6" fontWeight={900} sx={{ mb: 1, color: card.isDemo ? '#fff' : (isDark ? '#fff' : '#1a1a2e') }}>
          {card.title}
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 280, color: card.isDemo ? '#ccc' : (isDark ? '#bbb' : '#666') }}>
          {card.text}
        </Typography>
      </Box>
    </motion.div>
  );
}

const OnboardingTutorial = ({ userType = 'child', onComplete, demoMode = false }) => {
  const { isDark } = useThemeMode();
  const [cards, setCards] = useState(() => {
    const base = userType === 'parent' ? [...PARENT_CARDS] : [...CHILD_CARDS];
    return demoMode ? [...DEMO_CARDS, ...base] : base;
  });

  const handleDismiss = () => {
    setCards(prev => {
      const next = prev.slice(1);
      if (next.length === 0) {
        setTimeout(() => onComplete(), 300);
      }
      return next;
    });
  };

  const visibleCards = cards.slice(0, 3);
  const totalCards = demoMode
    ? (userType === 'parent' ? PARENT_CARDS.length : CHILD_CARDS.length) + DEMO_CARDS.length
    : (userType === 'parent' ? PARENT_CARDS.length : CHILD_CARDS.length);
  const currentIndex = totalCards - cards.length;

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      p: 2, bgcolor: isDark ? PINK.darkBg : PINK.bg,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Ambient orbs */}
      <Box sx={{
        position: 'absolute', top: '-20%', right: '-10%',
        width: 400, height: 400, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.main}15 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <Box sx={{
        position: 'absolute', bottom: '-15%', left: '-10%',
        width: 350, height: 350, borderRadius: '50%',
        background: `radial-gradient(circle, ${PINK.light}12 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 420 }}
      >
        {/* App icon */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Box component="img" src="/icons/icon-96x96.png" alt="icon-96x96.png" sx={{
            width: 48, height: 48, borderRadius: 2, mb: 2,
            boxShadow: PINK.glow,
          }} />
        </motion.div>

        {/* Card stack */}
        <Box sx={{
          position: 'relative', width: '100%', maxWidth: 380,
          height: 320, mb: 3,
        }}>
          <AnimatePresence mode="popLayout">
            {visibleCards.map((card, index) => (
              <TutorialCard
                key={`${card.title}-${currentIndex + index}`}
                card={card}
                index={index}
                isDark={isDark}
                onDismiss={handleDismiss}
              />
            ))}
          </AnimatePresence>
        </Box>

        {/* Dots */}
        <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
          {Array.from({ length: totalCards }).map((_, i) => (
            <Box
              key={i}
              sx={{
                width: i === currentIndex ? 20 : 8, height: 8, borderRadius: 4,
                bgcolor: i === currentIndex ? PINK.main : (isDark ? '#444' : `${PINK.main}33`),
                transition: 'all 0.3s',
              }}
            />
          ))}
        </Box>

        {/* Hint */}
        <Typography variant="caption" sx={{ mb: 2, color: isDark ? '#777' : PINK.dark + '88' }}>
          Pyyhkäise tai napauta korttia
        </Typography>

        {/* Skip */}
        <Chip
          label="Ohita"
          variant="outlined"
          size="small"
          onClick={onComplete}
          sx={{
            opacity: 0.5, borderColor: PINK.main + '44', color: isDark ? PINK.light : PINK.dark,
            '&:hover': { opacity: 1, borderColor: PINK.main },
          }}
        />
      </motion.div>
    </Box>
  );
};

export default OnboardingTutorial;
